import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Policies } from '@app/auth/auth-policies';
import { FormBuilderComponent as FormioBuilderComponent, FormioOptions, FormioRefreshValue } from '@formio/angular';
import { PerfectScrollbarComponent } from 'ngx-perfect-scrollbar';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { builderOptions } from './builder-options';
import { ClinicForm } from '@models/forms/clinic-form';
import { SharedForm } from '@models/forms/shared-form';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

type FormioBuilderInstance = {
  on: (event: string, callback: (form: any) => void) => void;
  getComponentById: (id: string) => any;
};
@Component({
  selector: 'app-form-builder',
  templateUrl: './form-builder.component.html',
  styleUrls: ['./form-builder.component.less'],
})
export class FormBuilderComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() form: ClinicForm | SharedForm;
  @Input() refreshBuilder: Observable<ClinicForm | SharedForm>;

  @ViewChild('builder') builder: FormioBuilderComponent;
  @ViewChild('json') jsonElement?: ElementRef;
  @ViewChild('code') codeElement?: ElementRef;
  @ViewChild(PerfectScrollbarComponent, { static: false }) perfectScrollbar: PerfectScrollbarComponent;

  unsub: Subject<void> = new Subject<void>();
  formId: number;
  formObj: Object;
  builderOptions = builderOptions as FormioOptions;
  developerPolicy = Policies.developer;

  constructor(private route: ActivatedRoute, private modalService: NgbModal) {}

  public refreshForm: EventEmitter<FormioRefreshValue> = new EventEmitter();

  ngOnInit() {
    this.route.params.pipe(takeUntil(this.unsub)).subscribe((params) => {
      this.formId = +params['id'];
    });

    this.refreshBuilder.pipe(takeUntil(this.unsub)).subscribe((form) => {
      this.form = form;
      this.updateBuilderForm();
    });

    this.initBuilder();
  }

  ngAfterViewInit() {
    // expose modalService globally just for runtime injection
    window['ngbModalInstance'] = this.modalService;

    // Use the builder's ready event
    (this.builder as any).ready.then(() => {
      const builderInstance = (this.builder as any).builder.instance;
      if (!builderInstance) return;

      // Listen for when a component is edited
      builderInstance.on('editComponent', (componentSchema: any) => {
        if (!builderInstance.editForm) return;

        const waitForEditForm = () => {
          const editForm = builderInstance.editForm;

          if (!editForm) return setTimeout(waitForEditForm, 200);

          // Wait until the modal edit form UI is rendered
          editForm.on('render', () => {
            const modalContent = document.querySelector('.formio-dialog-content');
            if (!modalContent) return;

            //   // Grab the container where the component inputs live
            const editContainer = modalContent.querySelector('.component-edit-container');
            if (!editContainer) return;
            // You can now call your custom afterEditForm() logic
            const sketchpadComp = builderInstance.preview.components.find(
              (c: any) => c.component.key === componentSchema.key
            );

            if (sketchpadComp?.type === 'sketchpad' && typeof sketchpadComp.afterEditForm === 'function') {
              sketchpadComp.afterEditForm(editForm);
            }

            // Cleanup handlers when modal closes
            const closeBtn = document.querySelector('.formio-dialog-close');
            const modal = document.querySelector('.formio-dialog-content');

            const cleanup = () => {
              console.log('Cleaning up Sketchpad modal edit handlers');
              editForm.off('change');
              sketchpadComp?.cleanupEditForm?.();
              closeBtn?.removeEventListener('click', cleanup);
              modal?.removeEventListener('keydown', handleEscape);
            };

            const handleEscape = (e: KeyboardEvent) => {
              if (e.key === 'Escape') cleanup();
            };

            closeBtn?.addEventListener('click', cleanup);
            modal?.addEventListener('keydown', handleEscape);
          });
        };

        waitForEditForm();
      });
    });
  }

  private initBuilder() {
    // Only set formObj once
    if (this.form?.formDefinition) {
      this.formObj = JSON.parse(this.form.formDefinition);
      if (this.jsonElement) {
        this.jsonElement.nativeElement.innerHTML = '';
        this.jsonElement.nativeElement.appendChild(document.createTextNode(this.form.formDefinition));
      }
    } else {
      this.formObj = { components: [] };
    }

    if (this.builder) {
      this.builder.form = this.formObj;
      this.builder.buildForm(this.formObj);
    }
  }

  private updateBuilderForm() {
    // Instead of rebuild, just patch components or emit refresh event
    this.builder.form = JSON.parse(JSON.stringify(this.form.formDefinition || '{}'));
    // optionally: this.builder.emitRefresh() or call a safe method to update the component values
  }

  onChange(event) {
    this.form.formDefinition = JSON.stringify(event.form, null, 4);

    if (this.jsonElement) {
      this.jsonElement.nativeElement.innerHTML = '';
      this.jsonElement.nativeElement.appendChild(document.createTextNode(JSON.stringify(event.form, null, 4)));
    }

    this.refreshForm.emit({
      property: 'form',
      value: event.form,
    });
  }

  ngOnDestroy() {
    this.unsub.next();
    this.unsub.complete();
  }
}
